import { createSlice } from "@reduxjs/toolkit";
import { get_req, post_req } from "../Utilities/apiUtil";
import { get_delivery_request_url } from "../Utilities/apiUrl";
import { errorActions } from "../store/errorSlice";
import { getBasketData } from "./basket";
import { whenNewsletterSaved } from "./userSlice";
import appConst from "../Utilities/appConst.json";
import { trackError } from "../Utilities/datadogErrorTracking";

// Helper function to map new API delivery structure to legacy format
const mapDeliveryToLegacyFormat = (delivery) => {
  return {
    id: delivery.item_id,
    estimatedDate: delivery.eta,
    name: delivery.invoice_name, // Used for SVG icon loading (e.g., "Asafta", "BHHome")
    displayName: delivery.invoice_name,
    isExpedited: Boolean(delivery.is_expedited),
    isAvailable: true,
    price: delivery.item_q1_price,
    priceForClubMember: delivery.item_final_price
  };
};

export const fetchShippingMethodsData = (basket) => {
  return async (dispatch, getState) => {
    // The club/newsletter shipping discount is account-based: get_delivery prices it
    // from the server's saved newsletter status. set_newsletter takes ~2.8s, so if the
    // user clicks through to shipping before it lands, get_delivery reads the stale
    // (non-member) price. Wait for any in-flight save first. No-ops if nothing pending.
    await whenNewsletterSaved();

    let safeBasket = basket;

    if (!Array.isArray(safeBasket?.items)) {
      safeBasket = getState().basket.basket;

      if (!Array.isArray(safeBasket?.items)) {
        // Empty basket detected on /shipping, BEFORE we try to recover it.
        trackError("shipping_empty_basket_detected", {
          errorType: "empty_basket_detected",
          page: appConst.FIFTH_PAGE,
          itemsState:
            safeBasket?.items === null
              ? "null"
              : safeBasket?.items === undefined
                ? "undefined"
                : typeof safeBasket?.items,
          basketKeys: Object.keys(safeBasket || {}), // [] = empty default {}, else partially-filled
          product: sessionStorage.getItem(appConst.PRODUCT), // source_type
          orderId: sessionStorage.getItem(appConst.ORDER_ID),
          guardIndex: getState().guard?.guardIndex, // how far in the flow the router thinks they are
        });

        const product = sessionStorage.getItem(appConst.PRODUCT);
        const isNewsletter = getState().user?.isNewsletter;
        await dispatch(getBasketData(product, isNewsletter));
        safeBasket = getState().basket.basket;
      }
    }

    if (!Array.isArray(safeBasket?.items)) {
      // Re-fetch didn't recover it either — don't POST a null-items basket (that is the -58 crash).
      // Mark the truly-broken case (so we can tell it apart from ones the re-fetch saved), then
      // flag a redirect to /basketItems. A component (BasketRedirectWatcher) does the actual
      // navigation with the router's own history — iframe-safe, no module-level custom history.
      trackError("shipping_empty_basket_recovery_failed", {
        errorType: "empty_basket_recovery_failed",
        page: appConst.FIFTH_PAGE,
        basketKeys: Object.keys(safeBasket || {}),
      });
      dispatch(shippingActions.setRedirectToBasketItems(true));
      return;
    }

    return post_req(get_delivery_request_url, safeBasket)
      .then((res) => {
        // Extract deliveries from nested response structure
        const deliveries = res?.payload?.price_list?.deliveries || [];
        const mappedDeliveries = deliveries.map(mapDeliveryToLegacyFormat);
        const sortedDeliveries = [...mappedDeliveries].sort((a, b) => {
          const aIsAsafta = String(a.id) === appConst.ASAFTA ? 0 : 1;
          const bIsAsafta = String(b.id) === appConst.ASAFTA ? 0 : 1;
          return aIsAsafta - bIsAsafta;
        });

        dispatch(shippingActions.setShippingMethods(sortedDeliveries));
      })
      .catch((error) => dispatch(errorActions.setError(true)));
  };
};

const shippingSlice = createSlice({
  name: "shipping",
  initialState: {
    shippingMethods: [],
    selectedId: -1,
    selectedShippingMethod: [],
    method: "",
    redirectToBasketItems: false,
  },
  reducers: {
    getShippingMethods: (state, action) => {
      return state;
    },
    setRedirectToBasketItems: (state, action) => {
      state.redirectToBasketItems = action.payload;
    },
    setShippingMethods: (state, action) => {
      state.shippingMethods = action.payload;
      if (action.payload && action.payload.length > 0) {
        const selectedShippingMethod = action.payload.find(
          (shippingMethod) => shippingMethod.id === state.selectedId
        );
        if (selectedShippingMethod) {
          state.selectedId = selectedShippingMethod.id;
        } else {
          state.selectedId = -1;
        }
      }
    },
    setSelectedId: (state, actiom) => {
      state.selectedId = actiom.payload.selectedId;
    },
    setSelectedShippingMethod: (state, actiom) => {
      state.selectedShippingMethod = actiom.payload.selectedShippingMethod;
    },
  },
});

export const shippingActions = shippingSlice.actions;
export default shippingSlice;
